import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	Row,
	Col,
	Button,
	FormGroup,
	Label,
	Input,
	Table,
	Badge,
} from "reactstrap";
import NumberFormat from "react-number-format";
import { CurrencyInput } from "features/forms/components/InputElement";
import { useForm } from "react-hook-form";

const DamageAssessment = ({ leaseEndData, updateLeaseEndData }) => {
	const methods = useForm();
	const [damages, setDamages] = useState(leaseEndData.damages || []);
	const [newDamage, setNewDamage] = useState({
		location: "",
		description: "",
		severity: "minor",
		estimatedCost: 0,
	});

	const damageSeverityOptions = [
		{ value: "minor", label: "Minor", multiplier: 1 },
		{ value: "moderate", label: "Moderate", multiplier: 1.5 },
		{ value: "major", label: "Major", multiplier: 2 },
	];

	const commonDamageLocations = [
		"Front Bumper",
		"Rear Bumper",
		"Front Driver Door",
		"Front Passenger Door",
		"Rear Driver Door",
		"Rear Passenger Door",
		"Hood",
		"Roof",
		"Trunk/Hatch",
		"Driver Side Mirror",
		"Passenger Side Mirror",
		"Windshield",
		"Interior - Dashboard",
		"Interior - Seats",
		"Interior - Carpet",
		"Wheels/Rims",
		"Tires",
		"Other",
	];

	const handleAddDamage = () => {
		if (newDamage.location && newDamage.description) {
			const updatedDamages = [
				...damages,
				{
					...newDamage,
					id: Date.now(),
					estimatedCost: parseFloat(newDamage.estimatedCost) || 0,
				},
			];
			setDamages(updatedDamages);
			setNewDamage({
				location: "",
				description: "",
				severity: "minor",
				estimatedCost: 0,
			});
		}
	};

	const handleRemoveDamage = (id) => {
		setDamages(damages.filter((d) => d.id !== id));
	};

	const totalDamageFee = damages.reduce(
		(sum, damage) => sum + parseFloat(damage.estimatedCost || 0),
		0
	);

	// Update parent component
	useEffect(() => {
		updateLeaseEndData({
			damages,
			totalDamageFee,
		});
	}, [damages, totalDamageFee]);

	const getSeverityColor = (severity) => {
		switch (severity) {
			case "minor":
				return "info";
			case "moderate":
				return "warning";
			case "major":
				return "danger";
			default:
				return "secondary";
		}
	};

	return (
		<div>
			<h4 className="mb-4">Damage Assessment</h4>
			<p className="text-muted mb-4">
				Document any damage beyond normal wear and tear. Charges will apply for
				excessive damage.
			</p>

			<Row>
				<Col md="6">
					<Card>
						<CardBody>
							<h5 className="mb-3">Add Damage</h5>

							<FormGroup>
								<Label>Damage Location *</Label>
								<Input
									type="select"
									value={newDamage.location}
									onChange={(e) =>
										setNewDamage({ ...newDamage, location: e.target.value })
									}
								>
									<option value="">Select Location...</option>
									{commonDamageLocations.map((loc) => (
										<option key={loc} value={loc}>
											{loc}
										</option>
									))}
								</Input>
							</FormGroup>

							<FormGroup>
								<Label>Description *</Label>
								<Input
									type="textarea"
									rows="3"
									value={newDamage.description}
									onChange={(e) =>
										setNewDamage({ ...newDamage, description: e.target.value })
									}
									placeholder="Describe the damage (e.g., 3-inch scratch, dent, tear, stain)"
								/>
							</FormGroup>

							<FormGroup>
								<Label>Severity</Label>
								<Input
									type="select"
									value={newDamage.severity}
									onChange={(e) =>
										setNewDamage({ ...newDamage, severity: e.target.value })
									}
								>
									{damageSeverityOptions.map((opt) => (
										<option key={opt.value} value={opt.value}>
											{opt.label}
										</option>
									))}
								</Input>
							</FormGroup>

							<FormGroup>
								<Label>Estimated Repair Cost</Label>
								<CurrencyInput
									{...methods}
									value={newDamage.estimatedCost}
									onChange={(val) =>
										setNewDamage({
											...newDamage,
											estimatedCost: parseFloat(val) || 0,
										})
									}
									name="estimatedCost"
									placeholder="0.00"
									disableValidation
								/>
								<small className="text-muted">
									Enter the estimated cost to repair this damage
								</small>
							</FormGroup>

							<Button
								color="primary"
								onClick={handleAddDamage}
								disabled={!newDamage.location || !newDamage.description}
								block
							>
								Add Damage Item
							</Button>
						</CardBody>
					</Card>
				</Col>

				<Col md="6">
					{/* Damage List */}
					<Card className="fee-summary-card">
						<CardBody>
							<div className="d-flex justify-content-between align-items-center mb-3">
								<h5 className="mb-0">Documented Damages</h5>
								<Badge color="primary">{damages.length} items</Badge>
							</div>

							{damages.length === 0 ? (
								<div className="text-center text-muted py-4">
									<p>No damages documented yet.</p>
									<small>
										If the vehicle is in good condition, proceed to the next
										step.
									</small>
								</div>
							) : (
								<>
									<div style={{ maxHeight: "400px", overflowY: "auto" }}>
										{damages.map((damage) => (
											<div key={damage.id} className="damage-item">
												<div className="damage-item-info">
													<div className="d-flex justify-content-between align-items-start mb-2">
														<strong>{damage.location}</strong>
														<Badge color={getSeverityColor(damage.severity)}>
															{damage.severity}
														</Badge>
													</div>
													<p className="mb-1 small text-muted">
														{damage.description}
													</p>
													<strong>
														<NumberFormat
															value={damage.estimatedCost}
															displayType="text"
															thousandSeparator
															prefix="$"
															decimalScale={2}
															fixedDecimalScale
														/>
													</strong>
												</div>
												<div className="damage-item-actions">
													<Button
														color="danger"
														size="sm"
														onClick={() => handleRemoveDamage(damage.id)}
													>
														Remove
													</Button>
												</div>
											</div>
										))}
									</div>

									<div className="fee-row mt-3">
										<span>Total Damage Fees:</span>
										<strong className="text-danger" style={{ fontSize: "1.3em" }}>
											<NumberFormat
												value={totalDamageFee}
												displayType="text"
												thousandSeparator
												prefix="$"
												decimalScale={2}
												fixedDecimalScale
											/>
										</strong>
									</div>
								</>
							)}
						</CardBody>
					</Card>

					{/* Damage Guidelines */}
					<Card className="mt-3">
						<CardBody>
							<h6>📋 Damage Assessment Guidelines</h6>
							<ul className="small mb-0">
								<li>
									<strong>Normal Wear:</strong> Minor scratches under 2 inches,
									light interior wear
								</li>
								<li>
									<strong>Minor Damage:</strong> Scratches 2-6 inches, small
									dents, minor stains ($50-$200)
								</li>
								<li>
									<strong>Moderate Damage:</strong> Dents, torn upholstery, large
									scratches ($200-$500)
								</li>
								<li>
									<strong>Major Damage:</strong> Panel replacement needed, major
									mechanical issues ($500+)
								</li>
							</ul>
						</CardBody>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

export default DamageAssessment;
